import { Hourglass } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { daysLeftPhrase, trialEndsPhrase } from '@/lib/trial';
import { cn } from '@/lib/utils';

/**
 * Days left of the free trial, in the top bar of every page.
 *
 * The dashboard has the full picture; this is so the number is never more than a glance away from
 * whatever somebody is doing. It links to billing, where the plans are, and it turns amber for the
 * last two days — the same point at which the dashboard starts suggesting a plan.
 */
export function TrialBadge() {
  const { trial } = useSubscriptionAccess();

  if (trial === null || trial.status !== 'running' || trial.days_left === null || trial.ends_at === null) return null;

  const urgent = trial.days_left <= 2;
  const days = daysLeftPhrase(trial.days_left);

  return (
    <Link
      to="/billing"
      title={`Free trial: ${trialEndsPhrase(trial.ends_at).toLowerCase()}`}
      aria-label={`Free trial, ${days}. Open billing`}
      className={cn(
        'mr-1 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors',
        urgent
          ? 'bg-warning/[0.12] text-warning hover:bg-warning/20 dark:bg-warning/10'
          : 'bg-primary/[0.10] text-primary hover:bg-primary/20 dark:bg-primary/10',
      )}
    >
      <Hourglass className="size-3.5" aria-hidden />
      <span className="hidden sm:inline">Free trial ·</span>
      <span className="tabular-nums">{days}</span>
    </Link>
  );
}
